import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import SaveIcon from '@material-ui/icons/Save';
import LinearProgress from '@material-ui/core/LinearProgress';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import FormHelperText from '@material-ui/core/FormHelperText';
import {
  compose,
  curry,
  curryN,
  find,
  isEmpty,
  isNil,
  mapObjIndexed,
  path,
  prop,
  replace,
  values,
} from 'ramda';
import { withApollo } from 'react-apollo';
import debounce from 'debounce';
import { GET_TEAM, CREATE_TEAM } from './queries';
import { splitCamelCase } from '../../utils';

const styles = () => ({
  container: {
    display: 'flex',
    flexFlow: 'column nowrap',
  },
  formRow: {
    display: 'flex',
    flexFlow: 'row nowrap',
  },
  formField: {
    flexGrow: '1',
    marginTop: '0.5em',
  },
  iconLeft: {
    marginRight: '0.25em',
  },
  fieldValidatingIcon: {
    height: '0.5em',
    width: '0.5em',
  },
  saveButton: {
    width: '6em',
  },
  mainError: {
    fontWeight: '600',
    fontSize: '1.25em',
  },
  loadingBar: {
    marginTop: '1em',
  },
});

class TeamCreator extends Component {
  state = {
    fields: {
      displayName: {
        label: 'Display Name',
        value: '',
        validating: false,
        error: null,
      },
    },
    loading: false,
    error: null,
  };

  setField = curry((name, field) => this.setState(state => ({
      fields: {
        ...state.fields,
        [name]: {
          ...state.fields[name],
          ...field,
        },
      },
      error: null,
    })),);

  validate = curryN(
    2,
    debounce(async (name, value) => {
      const { client } = this.props;
      this.setField(name, { validating: true });

      const {
        data: { team },
      } = await client.query({
        query: GET_TEAM,
        variables: { [name]: value },
      });

      if (!isNil(team)) {
        this.setField(name, {
          validating: false,
          error: new Error(
            `Team with ${splitCamelCase(name)} ${value} already exists.`,
          ),
        });
      } else {
        this.setField(name, {
          validating: false,
          error: null,
        });
      }

      return null;
    }),
    300,
  );

  handleChange = curry(async (name, value) => {
    this.setField(name, { value });
    this.validate(name, value);
  });

  save = async () => {
    const { client, match } = this.props;
    const { fields } = this.state;
    const invalidField = find(({ error }) => !isNil(error), values(fields));
    const emptyField = find(
      ({ value }) => isNil(value) || isEmpty(value),
      values(fields),
    );

    if (!isNil(invalidField)) {
      this.setState({
        error: new Error('Please fix all errors before saving'),
      });
    } else if (!isNil(emptyField)) {
      this.setState({
        error: new Error(`${emptyField.label} must not be empty`),
      });
    } else {
      this.setState({ loading: true });

      try {
        const team = {
          display_name: fields.displayName.value,
        };
        await client.mutate({
          mutation: CREATE_TEAM,
          variables: { team },
        });
        const redirect = replace('/new', '', match.url);
        window.location.replace(redirect);
      } catch (error) {
        this.setState({
          loading: false,
          error: new Error(`Unable to create team: ${error.message}`),
        });
      }
    }
  };

  render() {
    const { classes } = this.props;
    const { error, fields, loading } = this.state;

    return (
      <Fragment>
        {error && (
          <FormControl className={classes.formField} error={!isNil(error)}>
            <FormHelperText id="team-error" className={classes.mainError}>
              {prop('message', error)}
            </FormHelperText>
          </FormControl>
        )}
        <form className={classes.container} noValidate autoComplete="off" onSubmit={this.save}>
          {values(
            mapObjIndexed(
              (field, name) => (
                <div key={name} className={classes.formRow}>
                  <FormControl
                    className={classes.formField}
                    error={!isNil(field.error)}
                  >
                    <InputLabel htmlFor="component-error">
                      {field.label}
                    </InputLabel>
                    <Input
                      id={`team-${name}`}
                      value={field.value}
                      className={classes.formField}
                      onChange={compose(
                        this.handleChange(name),
                        path(['target', 'value']),
                      )}
                      variant="outlined"
                    />
                    <FormHelperText id={`team-${name}-error`}>
                      {path(['error', 'message'], field)}
                    </FormHelperText>
                  </FormControl>
                  {field.validating && (
                    <CircularProgress className={classes.fieldValidatingIcon} />
                  )}
                </div>
              ),
              fields,
            ),
          )}
          <br />
          <Button
            variant="contained"
            aria-label="Save"
            onClick={this.save}
            disabled={loading}
            className={classes.saveButton}
          >
            <SaveIcon className={classes.iconLeft} />
            Save
          </Button>
          {loading && <LinearProgress className={classes.loadingBar} />}
        </form>
      </Fragment>
    );
  }
}

TeamCreator.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withApollo(withStyles(styles)(TeamCreator));
